import React, { useCallback, useEffect, useState } from "react";
import {createFTPCredential, getStoreById, updateFTPSettings} from "../../api/admin.js";
import Loader from "../../components/Loader.jsx";
import { PrimaryButton } from "../../components/Buttons.jsx";
import { addToast } from "../../store/app/action.js";
import { useDispatch } from "react-redux";

const internalMap = {
  sku: "Retailer Sku",
  upc: "UPC",
  price: "Source Price",
  sell_price: "Sell Price",
  quantity: "Quantity",
  discontinued: "Disabled",
  title: "Product Name",
  last_sold: "Last Sold",
  category: "Category",
};
/// internal map but value blanked out
const cleanMap = ((internalMap) => {
  return Object.keys(internalMap).reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});
})(internalMap);

const InventoryFeed = ({ storeID }) => {
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importSettings, setImportSettings] = useState({});
  const [hasCredentials, setHasCredentials] = useState(false);
  const dispatch = useDispatch();
  const [mapping, setMapping] = useState(JSON.parse(JSON.stringify(cleanMap)));
  const [processing, setProcessing] = useState(false);
  const submit = async () => {
    const payload = {
      ...importSettings,
    };
    Object.keys(cleanMap).forEach((key) => {
      if (mapping[key]) {
        payload[key] = mapping[key];
      }
    });
    setProcessing(true);
    try {
      await updateFTPSettings(store.store_id, payload);
      dispatch(
        addToast({
          type: "success",
          message: "Settings saved successfully",
          timer: 5,
        })
      );
    }catch(e){
      let message = "please try again";
      try { 
        const serverMessage = e.response?.data?.error
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}

      dispatch(
        addToast({
          type: "error",
          message: "Error updating settings, " + message,
          timer: 5,
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const [connectionSettings, setConnectionSettings] = useState([]);

  useEffect(() => {
    setHasCredentials(Boolean(importSettings?.credentials?.host));
    setConnectionSettings([
      { value: importSettings?.credentials?.host, label: "Host" },
      { value: importSettings?.credentials?.user, label: "User" },
      {
        value: importSettings?.credentials?.password,
        label: "Password",
      },
      { value: importSettings?.credentials?.path, label: "Path" },
    ]);
  }, [importSettings]);

  const loadStore = useCallback(async () => {
    setLoading(true);
    const store = await getStoreById(storeID);
    setStore(store.store);
    if(store.store.import_settings?.credentials?.path && !store.store.import_settings?.credentials?.processed_output_path){
      store.store.import_settings.credentials.processed_output_path = store.store.import_settings.credentials.path;
    }
    setImportSettings(store.store.import_settings || {});
    setLoading(false);
    setMapping(store.store.import_settings || cleanMap);
  }, []);

  useEffect(() => {
    loadStore();
  }, []);
  return (
    <div className="py-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Inventory Feed Settings</h2>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {hasCredentials && (
            <div>
              <div className="w-full text-center py-4 gap-2 flex justify-start items-start">
                <div className="border-2 flex flex-col pr-4 py-2 space-y-2 text-sm">
                  <h2 className="text-xl font-bold flex pl-4 pb-4">
                    CSV Mapping
                  </h2>
                  <div className="flex font-bold">
                    <div className="w-36 justify-end items-center flex">
                      Field
                    </div>
                    <div className="w-12 justify-center items-center flex">
                      →
                    </div>
                    <div className="w-48 flex justify-start">
                      CSV Column Name
                    </div>
                  </div>
                  {Object.keys(internalMap).map((key) => {
                    return (
                      <div className="flex" key={key}>
                        <div className="w-36 justify-end items-center flex">
                          {internalMap[key]}
                        </div>
                        <div className="w-12 justify-center items-center flex">
                          →
                        </div>
                        <div className="w-48">
                          <input
                            placeholder={internalMap[key]}
                            value={mapping[key]}
                            className="w-full text-sm"
                            onChange={(e) => {
                              setMapping({
                                ...mapping,
                                [key]: e.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col justify-end h-full gap-2">
                  <div className="border-2 flex flex-col pr-4 py-2 space-y-2 text-sm flex-1">
                    <h2 className="text-xl font-bold flex pl-4 pb-4">
                      FTP Connection Settings
                    </h2>
                    {connectionSettings.map((setting) => {
                      return (
                        <div className="flex" key={setting.label}>
                          <div className="w-36 justify-end items-center flex pr-4">
                            {setting.label}
                          </div>
                          <div className="w-64 pr-2">
                            <input
                              value={setting.value}
                              className="w-full text-sm"
                              disabled
                            />
                          </div>
                          <PrimaryButton
                            size={"xs"}
                            onClick={() => {
                              navigator.clipboard.writeText(`${setting.value}`);
                              dispatch(
                                addToast({
                                  type: "success",
                                  message: `${setting.label} copied to clipboard`,
                                  timer: 3,
                                })
                              );
                            }}
                          >
                            Copy
                          </PrimaryButton>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-2 flex flex-col pr-4 py-2 space-y-2 text-sm">
                    <h2 className="text-xl font-bold flex pl-4 pb-4">
                      Inventory sync settings
                    </h2>
                    <div className="pl-4 space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={"disable_unmatched"}
                          value={importSettings?.disable_unmatched}
                          checked={importSettings?.disable_unmatched}
                          onChange={(event) => {
                            setImportSettings({
                              ...importSettings,
                              disable_unmatched: event.target.checked,
                            });
                          }}
                          className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                        />
                        <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                          Disable Unmatched Items
                        </span>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={"disable_unmatched"}
                          value={importSettings?.sync_upc}
                          checked={importSettings?.sync_upc}
                          onChange={(event) => {
                            setImportSettings({
                              ...importSettings,
                              sync_upc: event.target.checked,
                            });
                          }}
                          className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                        />
                        <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                          Sync UPC
                        </span>
                      </div>

                      <div className="flex">
                        <div className="w-36 justify-end items-center flex pr-4">
                          Quantity Threshold
                        </div>
                        <div className="w-24 pr-2">
                          <input
                            value={importSettings?.quantity_threshold}
                            onChange={(e) => {
                              setImportSettings({
                                ...importSettings,
                                quantity_threshold: parseInt(
                                  e.target.value,
                                  10
                                ),
                              });
                            }}
                            type="number"
                            className="w-full text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <PrimaryButton loading={processing} onClick={submit}>
                  Save
                </PrimaryButton>
              </div>
            </div>
          )}
          {!hasCredentials && (
            <div className="w-full text-center py-4 space-y-4">
              <div className="text-sm text-gray-500">
                Inventory sync currently disabled, click below to create FTP
                credentials
              </div>
              <PrimaryButton
                size={"sm"}
                loading={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const _store = await createFTPCredential(store.store_id);
                    setStore(_store.seller);
                    setHasCredentials(true);
                    dispatch(
                      addToast({
                        type: "success",
                        message: "FTP credentials created successfully",
                      })
                    );
                  } catch (e) {
                    let message = "An error occurred";
                    try {
                      const serverMessage = e.response?.data?.message;
                      if (serverMessage) {
                        message = serverMessage;
                      }
                    } catch {}
                    message += ", please try again.";
                    dispatch(
                      addToast({
                        type: "error",
                        message: message,
                      })
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Create FTP Credentials
              </PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InventoryFeed;
